export const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'https://editor-worker.rulerbdsm.com';

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function concatBytes(a, b) {
  const result = new Uint8Array(a.length + b.length);
  result.set(a, 0);
  result.set(b, a.length);
  return result;
}

function bytesToBase64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}

export async function fetchTemplate(mode) {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error('Web Crypto API is not available. Please use a modern browser with HTTPS.');
  }
  const clientNonce = window.crypto.getRandomValues(new Uint8Array(32));
  const response = await fetch(`${WORKER_URL}/api/svg-data`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode, clientNonce: bytesToBase64(clientNonce) }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const data = await response.json();
  if (data.clientNonce !== bytesToBase64(clientNonce)) {
    throw new Error('Client nonce mismatch — possible replay attack');
  }
  return data;
}

export async function deriveSessionKey(serverNonceB64, clientNonceB64, wrappedKeyB64, mode) {
  const serverNonce = base64ToBytes(serverNonceB64);
  const clientNonce = base64ToBytes(clientNonceB64);
  const wrappedKey = base64ToBytes(wrappedKeyB64);
  const secretBytes = (function() {
    const _a = new Uint8Array([0x4e,0x32,0x82,0x8a,0xda,0xaa,0x46,0x27,0xc1,0x28,0xd9,0xf4,0x85,0x63,0x35,0x7c,0x24,0x4b,0xd2,0x94,0x46,0x9d,0x20,0x02,0xdc,0xb7,0x87,0xf6,0x4f,0x52,0x52,0x7e]);
    const _b = new Uint8Array([0x4e,0xf6,0x8d,0x8a,0xc2,0x46,0x4a,0xd8,0x6b,0x97,0x35,0x6a,0x7d,0x1e,0x51,0x1c,0x8c,0x96,0x7d,0xf4,0xf2,0xc7,0x4c,0xde,0x55,0xb2,0x48,0x27,0xec,0x80,0x8a,0xdf]);
    const _r = new Uint8Array(_a.length);
    for (let _i = 0; _i < _a.length; _i++) _r[_i] = _a[_i] ^ _b[_i];
    return _r;
  })();

  const hmacKey = await window.crypto.subtle.importKey(
    'raw', secretBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false, ['sign']
  );

  const hmacInput = concatBytes(concatBytes(serverNonce, clientNonce), new TextEncoder().encode(mode));
  const hmacSig = await window.crypto.subtle.sign('HMAC', hmacKey, hmacInput);
  const hmacBytes = new Uint8Array(hmacSig);

  const sessionKey = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    sessionKey[i] = wrappedKey[i] ^ hmacBytes[i];
  }

  return window.crypto.subtle.importKey(
    'raw', sessionKey,
    { name: 'AES-GCM' },
    false, ['decrypt']
  );
}

export async function decryptTemplate(ciphertextB64, key, ivB64) {
  const ciphertext = Uint8Array.from(atob(ciphertextB64), c => c.charCodeAt(0));
  const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));

  const plaintext = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  const json = new TextDecoder().decode(plaintext);
  return JSON.parse(json);
}
